import {
  Box,
  FormControl,
  FormHelperText,
  Slider,
  SliderThumb,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { LoadingIcon } from './Icons';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import './RangeSlider.css';
import { USER_TYPE } from 'constant/app.config';

export const PriceBracketChat = ({ handleInputChange, value }) => {
  const theme = useTheme();
  const sliderRef = useRef(null);

  const parseInitialValue = (val) => {
    if (typeof val === 'string') {
      const parts = val.split('-').map(Number);
      return parts.length === 2 ? parts : [600000, 2600000];
    } else if (typeof val === 'number') {
      return val;
    }
    return [200000000, 600000000];
  };

  const parseMaximumValue = (val) => {
    if (typeof val === 'string') {
      const parts = val.split('-').map(Number);
      return parts[0] > 999999999 || parts[1] > 999999999
        ? parts[1] + 10000000
        : 999999999;
    } else if (typeof val === 'number') {
      return val > 999999999 ? val + 10000000 : 999999999;
    }
    return 999999999;
  };

  const parsedRange = parseInitialValue(value);

  const [sliderValue, setSliderValue] = useState(parsedRange);

  const BUFFER_PERCENT = 50; // user can move ±50% initially

  const getBufferedRange = ([min, max]) => {
    const span = max - min || 100;
    const buffer = Math.round((span * BUFFER_PERCENT) / 100);

    return {
      min: Math.max(0, min - buffer),
      max: max + buffer,
    };
  };

  const [sliderBounds, setSliderBounds] = useState(() =>
    getBufferedRange(parsedRange)
  );
  const [maximumSliderValue, setMaximumSliderValue] = useState(() =>
    parseMaximumValue(value)
  );

  useEffect(() => {
    if (!value || typeof value !== 'string' || !value.includes('-')) return;
    const parts = value.split('-').map(Number);
    if (parts[0] > 999999999 || parts[1] > 999999999) {
      setMaximumSliderValue(parts[1] + 100000000);
    }
  }, [sliderValue]);

  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const prevValueRef = useRef(sliderValue);

  // useEffect(() => {
  //   const data = `${sliderValue[0]}-${sliderValue[1]}`;
  //   handleInputChange(data);
  // }, [sliderValue]);

  useEffect(() => {
    const toPlainNumber = (num) => {
      if (!Number.isFinite(num)) return '0';

      if (num >= 1000000) {
        // round to 0.1 Million
        return String(Math.round(num / 100000) * 100000);
      }

      if (num >= 1000) {
        // round to 0.1 Thousand
        return String(Math.round(num / 100) * 100);
      }

      return String(Math.round(num));
    };

    const data = `${toPlainNumber(sliderValue[0])}-${toPlainNumber(
      sliderValue[1]
    )}`;

    handleInputChange(data);
  }, [sliderValue]);

  // const formatValue = (val) => {
  //   if (val < 1000) {
  //     return `$${val}`;
  //   } else if (val < 50000) {
  //     return `$${parseFloat((val / 1000).toFixed(1))}k`;
  //   } else if (val < 1000000) {
  //     return `$${parseFloat((val / 1000).toFixed(1))}k`;
  //   } else {
  //     return `$${parseFloat((val / 1000000).toFixed(1))}M`;
  //   }
  // };

  const formatValue = (val) => {
    let result;

    if (val < 1000) {
      result = `$${val}`;
    } else if (val < 1000000) {
      result = `$${(val / 1000).toFixed(1)}k`;
    } else {
      result = `$${(val / 1000000).toFixed(1)}M`;
    }

    // ✅ Remove trailing .0 only
    result = result.replace('.0k', 'k').replace('.0M', 'M');

    return result;
  };
  // const getTooltipStyles = (index) => {
  //   if (!sliderRef.current) return {};

  //   const value = sliderValue[index];
  //   const percentage = (value / maximumSliderValue) * 100;

  //   let transform = 'translateX(-50%)';
  //   let left = `calc(${percentage}% - 0px)`;

  //   if (index === 0) {
  //     transform = 'translateX(-90%)';
  //   } else if (index === 1) {
  //     transform = 'translateX(5%)';
  //   }

  //   return {
  //     position: 'absolute',
  //     top: '33px',
  //     left,
  //     transform,
  //     color: '#333',
  //     fontSize: '14px',
  //     padding: '4px 6px',
  //     fontFamily: 'Poppins, sans-serif',
  //     whiteSpace: 'nowrap',
  //     pointerEvents: 'none',
  //     zIndex: 1,
  //   };
  // };

  const getTooltipStyles = (index) => {
    if (!sliderRef.current) return {};

    const sliderWidth = sliderRef.current.clientWidth;
    const THUMB_SIZE = 26;
    const usableWidth = sliderWidth - THUMB_SIZE;
    const startOffset = THUMB_SIZE / 2;

    const range = sliderBounds.max - sliderBounds.min;

    const leftX =
      startOffset + ((sliderValue[0] - sliderBounds.min) / range) * usableWidth;

    const rightX =
      startOffset + ((sliderValue[1] - sliderBounds.min) / range) * usableWidth;

    const LABEL_WIDTH = 70;
    const MIN_GAP = 4;

    let x = index === 0 ? leftX : rightX;

    if (Math.abs(rightX - leftX) < LABEL_WIDTH + MIN_GAP) {
      x = index === 0 ? leftX - LABEL_WIDTH * 0.6 : rightX + LABEL_WIDTH * 0.1;
    }

    return {
      position: 'absolute',
      top: '45px',
      left: `${x}px`,
      transform: 'translateX(-50%)',
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 10,
    };
  };

  const getStepForValue = (val) => {
    if (val <= 10) return 1;
    if (val <= 100) return 5;
    if (val <= 1000) return 50;
    if (val <= 10000) return 1000;
    if (val < 50000) return 5000;
    if (val < 100000) return 10000;
    if (val < 500000) return 50000;
    if (val < 1000000) return 250000;
    if (val < 2500000) return 100000;
    if (val < 4000000) return 250000;
    if (val < 10000000) return 1000000;
    return 2500000;
  };
  const handleSliderChange = (newValue) => {
    const [newMin, newMax] = newValue;

    setSliderBounds((prev) => {
      let updatedMin = prev.min;
      let updatedMax = prev.max;

      const range = prev.max - prev.min;
      const EDGE_THRESHOLD = range * 0.01;

      // 🔥 FIXED gradual increment (10M per hit)
      const EXPAND_AMOUNT = 10000000; // 10 million

      if (newMax >= prev.max - EDGE_THRESHOLD) {
        updatedMax = prev.max + EXPAND_AMOUNT;
      }

      if (newMin <= prev.min + EDGE_THRESHOLD) {
        updatedMin = Math.max(0, prev.min - EXPAND_AMOUNT);
      }

      return {
        min: updatedMin,
        max: updatedMax,
      };
    });

    setSliderValue(newValue);
  };
  return (
    <Stack
      direction="row"
      gap="10px"
      width="100%"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        minWidth="55%"
        maxWidth="90%"
        position="relative"
        ref={sliderRef}
        id="range-slider-container"
        width={matchessm ? '100%' : '100%'}
      >
        <div style={getTooltipStyles(0)}>{formatValue(sliderValue[0])}</div>
        <div style={getTooltipStyles(1)}>{formatValue(sliderValue[1])}</div>

        <RangeSlider
          id="range-slider-ab"
          min={sliderBounds.min}
          max={sliderBounds.max}
          value={sliderValue}
          onInput={handleSliderChange}
          style={{
            width: '100%',
            height: '10px',
            backgroundColor: '#ffffff',
            border: '1px solid #e7e5e5',
            borderRadius: '5px',
            padding: '20px 0',
            '--range-slider-track-height': '10px',
            '--range-slider-track-bg': '#ffffff',
            '--range-slider-track-border': '1px solid #e7e5e5',
            '--range-slider-thumb-size': '26px',
            '--range-slider-thumb-bg': 'white',
            '--range-slider-thumb-border': '1px solid #e7e5e5',
            '--range-slider-thumb-shadow': '0px 1px 8px rgba(0,0,0,0.15)',
            '--range-slider-thumb-radius': '50%',
            '--range-slider-thumb-background-image': `url("data:image/svg+xml;utf8,<svg width='10' height='10' viewBox='0 0 10 10' xmlns='http://www.w3.org/2000/svg'><rect x='2' y='2' width='1.5' height='6' fill='%230065fc'/><rect x='6.5' y='2' width='1.5' height='6' fill='%230065fc'/></svg>")`,
            '--range-slider-thumb-background-repeat': 'no-repeat',
            '--range-slider-thumb-background-position': 'center',
            '--range-slider-thumb-background-size': '10px 10px',
          }}
        />
      </Box>
    </Stack>
  );
};

export const PropertyWorthChat = ({ handleInputChange, value }) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const sliderRef = useRef(null);

  const parseInitialValue = (val) => {
    if (typeof val === 'string') {
      const parts = val.split('-').map(Number);
      return parts.length === 2
        ? Math.round((parts[0] + parts[1]) / 2)
        : parts[0];
    }
    if (typeof val === 'number') return val;
    return 450000;
  };

  const initialValue = parseInitialValue(value);

  const [sliderValue, setSliderValue] = useState(initialValue);
  const prevValueRef = useRef(initialValue);

  const BUFFER_PERCENT = 50;

  const getBufferedRange = (val) => {
    const buffer = Math.max(10000, Math.round((val * BUFFER_PERCENT) / 100));
    return {
      min: Math.max(1, val - buffer),
      max: val + buffer,
    };
  };

  const [sliderBounds, setSliderBounds] = useState(() =>
    getBufferedRange(initialValue)
  );

  // useEffect(() => {
  //   handleInputChange(sliderValue);
  // }, [sliderValue]);
  useEffect(() => {
    const toPayloadValue = (num) => {
      if (!Number.isFinite(num)) return 0;

      // Match EXACTLY with display logic
      if (num >= 1000000) {
        return Number((num / 1000000).toFixed(1)) * 1000000;
      }

      if (num >= 1000) {
        return Number((num / 1000).toFixed(1)) * 1000;
      }

      return Math.round(num);
    };

    handleInputChange(toPayloadValue(sliderValue));
  }, [sliderValue]);

  const formatValue = (val) => {
    let result;

    if (val < 1000) {
      result = `$${val}`;
    } else if (val < 1000000) {
      result = `$${(val / 1000).toFixed(1)}k`;
    } else {
      result = `$${(val / 1000000).toFixed(1)}M`;
    }

    // ✅ Remove trailing .0 only
    result = result.replace('.0k', 'k').replace('.0M', 'M');

    return result;
  };

  const getTooltipStyles = () => {
    const range = sliderBounds.max - sliderBounds.min;

    if (range <= 0) return {};

    const percent = ((sliderValue - sliderBounds.min) / range) * 100;

    return {
      position: 'absolute',
      top: '45px',
      left: `${percent}%`,
      transform: 'translateX(-50%)',
      fontFamily: 'Poppins',
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 10,
    };
  };

  const getStepForValue = (val) => {
    if (val < 100000) return 1000;
    if (val < 1000000) return 50000;
    if (val < 10000000) return 250000;
    return 1000000;
  };

  // 🔹 Smooth drag (NO snapping here)
  const handleChange = (newValue) => {
    const raw = newValue[1];

    prevValueRef.current = sliderValue;
    setSliderValue(raw);

    const step = getStepForValue(raw);
    const expand = step * 10;

    if (raw >= sliderBounds.max - step) {
      setSliderBounds((prev) => ({
        min: prev.min,
        max: prev.max + expand,
      }));
    }

    if (raw <= sliderBounds.min + step) {
      setSliderBounds((prev) => ({
        min: Math.max(1, prev.min - expand),
        max: prev.max,
      }));
    }
  };

  const SNAP_THRESHOLD = 0.6;

  const handleDragEnd = () => {
    setSliderValue((current) => {
      const step = getStepForValue(current);
      const prev = prevValueRef.current;

      const lower = Math.floor(current / step) * step;
      const upper = lower + step;

      const distFromLower = (current - lower) / step; // 0 → 1
      const distFromUpper = (upper - current) / step; // 0 → 1

      // 👉 Forward drag
      if (current > prev) {
        return Math.max(
          sliderBounds.min,
          distFromLower >= SNAP_THRESHOLD ? upper : current
        );
      }

      // 👉 Backward drag
      return Math.max(
        sliderBounds.min,
        distFromUpper >= SNAP_THRESHOLD ? lower : current
      );
    });
  };

  return (
    <Stack
      direction="row"
      gap="10px"
      width="100%"
      justifyContent="center"
      alignItems="center"
    >
      <Box
        minWidth="45%"
        maxWidth="90%"
        position="relative"
        ref={sliderRef}
        width={matchessm ? '100%' : '80%'}
      >
        <div style={getTooltipStyles()}>{formatValue(sliderValue)}</div>

        <RangeSlider
          min={sliderBounds.min}
          max={sliderBounds.max}
          value={[sliderBounds.min, sliderValue]}
          onInput={handleChange}
          onThumbDragEnd={handleDragEnd}
          className="custom-single-slider"
          style={{
            width: '100%',
            backgroundColor: '#ffffff',
            border: '1px solid #e7e5e5',
            borderRadius: '5px',
            padding: '20px 0',
            '--range-slider-track-height': '10px',
            '--range-slider-track-bg': '#ffffff',
            '--range-slider-track-border': '1px solid #e7e5e5',
            '--range-slider-thumb-size': '26px',
            '--range-slider-thumb-bg': 'white',
            '--range-slider-thumb-border': '1px solid #e7e5e5',
            '--range-slider-thumb-shadow': '0px 1px 8px rgba(0,0,0,0.15)',
          }}
        />
      </Box>
    </Stack>
  );
};

function valueLabelFormat(slidervalue) {
  const units = '$';
  return `${units}${slidervalue}`;
}

export const ChatLoading = ({ value, agentProfileImage }) => {
  const isAgentOrVendor = USER_TYPE === 'Agent' || USER_TYPE === 'Vendor';
  const onlySmallScreen = useMediaQuery((theme) =>
    theme.breakpoints.down('md')
  );
  return (
    <Stack
      direction={'row'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={'20px'}
    >
      <Box
        sx={{
          width: onlySmallScreen ? '276px' : '550px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          sx={{
            background: 'rgba(228, 228, 228, 1)',
            width: onlySmallScreen ? '126px' : '176px',
            height: onlySmallScreen ? '54px' : '84px',
            borderRadius: '200px',
            display: 'flex',
            justifyContent: 'space-evenly',
            alignItems: 'center',
          }}
        >
          <Typography className="loading-dot">
            <LoadingIcon onlySmallScreen={onlySmallScreen} />
          </Typography>
          <Typography className="loading-dot">
            <LoadingIcon onlySmallScreen={onlySmallScreen} />
          </Typography>
          <Typography className="loading-dot">
            <LoadingIcon onlySmallScreen={onlySmallScreen} />
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          height: onlySmallScreen ? '40px' : '56px',
          width: onlySmallScreen ? '40px' : '56px',
          ...(isAgentOrVendor
            ? {
                background: 'white',
                border: '2px solid',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }
            : {
                background: 'green',
              }),
          borderRadius: '50%',
          overflow: 'hidden',

          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          color: 'white',
          fontWeight: 600,
          fontFamily: 'Poppins',
          fontSize: onlySmallScreen ? '14px' : '18px',
          flexShrink: 0, // important
        }}
      >
        {agentProfileImage?.trim() ? (
          <img
            alt="Logo"
            src={agentProfileImage}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Poppins',
              fontSize: { xs: '14px', sm: '16px', md: '18px' },
              color: isAgentOrVendor ? '#112347' : 'white',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export const Calendar = ({
  onDateSelect,
  errors,
  setErrors,
  value,
  disablefuture,
  disablepast,
}) => {
  const [selectedDate, setSelectedDate] = useState(value || null);
  useEffect(() => {
    setSelectedDate(value || null);
  }, [value]);
  const Dateicon = () => {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8 2V5"
          stroke="#200E32"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M16 2V5"
          stroke="#200E32"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M3.5 9.08984H20.5"
          stroke="#200E32"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
          stroke="#200E32"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15.6937 13.6992H15.7027"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15.6937 16.6992H15.7027"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M11.9945 13.6992H12.0035"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M11.9945 16.6992H12.0035"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.29529 13.6992H8.30427"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.29529 16.6992H8.30427"
          stroke="#200E32"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );
  };

  return (
    <FormControl fullWidth>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{
            '& .MuiFormLabel-root': {
              fontFamily: 'Poppins !important',
              fontSize: '16px !important',
              fontWeight: '300',
            },
            '& .MuiButtonBase-root .MuiPickersDay-root .MuiPickersDay-dayWithMargin':
              {
                fontFamily: 'Poppins !important',
              },
            '& .MuiPickersCalendarHeader-label': {
              fontFamily: 'Poppins !important',
            },
            '& .MuiInputBase-input': {
              fontFamily: 'Poppins !important',
              fontSize: '16px !important',
              fontWeight: '300',
            },
            '& .MuiInputLabel-root': {
              fontFamily: 'Poppins !important',
              fontSize: '16px !important',
              fontWeight: '300',
            },
            '& .MuiSvgIcon-root ': {
              fill: '#212529 !important',
            },
            '.MuiOutlinedInput-notchedOutline': {
              border: '1px solid #0065FC',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #0065FC',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #0065FC',
            },
          }}
          inputProps={{
            style: {
              fontFamily: 'Poppins',
            },
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              error: errors,
            },
          }}
          format="DD-MM-YYYY"
          disableFuture={disablefuture}
          disablePast={disablepast}
          value={selectedDate ? dayjs(selectedDate) : null}
          onChange={(newValue) => {
            // Keep formatted string for UI & storage
            const formattedDate = newValue ? newValue : null;

            // --- VALIDATION FOR FUTURE DATES ---
            if (disablefuture && newValue && newValue.isAfter(dayjs(), 'day')) {
              setErrors('Date should not be in the future.');
              return;
            }

            // --- VALIDATION FOR PAST DATES ---
            if (disablepast && newValue && newValue.isBefore(dayjs(), 'day')) {
              setErrors('Date should not be in the past.');
              return;
            }

            // VALID DATE SELECTED
            setSelectedDate(formattedDate);

            if (onDateSelect) {
              onDateSelect(formattedDate); // sending DD-MM-YYYY to parent
            }

            // Empty or null date
            setErrors(!formattedDate ? 'Please select a date.' : '');
          }}
          slots={{
            openPickerIcon: Dateicon,
          }}
        />
      </LocalizationProvider>
      {errors && (
        <FormHelperText sx={{ color: '#c02424', fontFamily: 'Poppins' }}>
          {errors}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const TextBoxChat = ({ handleInputChange, error, setError, value }) => {
  const handleChange = (event) => {
    const value = event.target.value;
    handleInputChange(value);
    setError(false);
  };

  return (
    <Stack spacing={1} width="100%">
      <Stack
        direction={'row'}
        gap={'10px'}
        width={'100%'}
        alignItems={'center'}
        boxShadow={'none !important'}
      >
        <Box width={'90%'}>
          <TextField
            variant="outlined"
            type="text"
            fullWidth
            placeholder="Enter here..."
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'Poppins',
              },
            }}
            onChange={handleChange}
            size="small"
            error={error}
            value={value ? value : null}
            helperText={error ? 'This field is required' : ''}
          />
        </Box>
      </Stack>
    </Stack>
  );
};
